/**
 * Boorhub 3D forest stage — dense procedural woods as full-viewport background.
 * Camera walks deeper through the forest with scroll; pointer looks around.
 */
import * as THREE from "three";

export type SceneApi = {
  setProgress: (t: number) => void;
  setActiveService: (index: number) => void;
  setTheme: (theme: "light" | "dark") => void;
  resize: () => void;
  dispose: () => void;
};

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function isMobile(): boolean {
  return window.matchMedia("(max-width: 768px)").matches || window.innerWidth < 768;
}

function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createBoorhubScene(
  container: HTMLElement,
  options: { onReady?: () => void; theme?: "light" | "dark" } = {},
): SceneApi {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let mobile = isMobile();
  let theme: "light" | "dark" = options.theme ?? "dark";

  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const renderer = new THREE.WebGLRenderer({
    antialias: !mobile,
    alpha: false,
    powerPreference: mobile ? "low-power" : "high-performance",
  });
  // Cap fill rate; the forest does not need retina-resolution geometry.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 0.85 : 1.25));
  renderer.setClearColor(theme === "light" ? 0xdde8df : 0x0a1410, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = theme === "light" ? 1.15 : 1.05;
  container.appendChild(renderer.domElement);
  // pan-y: vertical scroll always wins; orbit only with mouse drag on desktop
  renderer.domElement.style.cssText =
    "position:absolute;inset:0;width:100%;height:100%;display:block;touch-action:pan-y;";

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(theme === "light" ? 0xc5d8c8 : 0x0c1a14, theme === "light" ? 0.02 : 0.028);
  scene.background = new THREE.Color(theme === "light" ? 0xdde8df : 0x0a1410);

  const camera = new THREE.PerspectiveCamera(mobile ? 58 : 48, 1, 0.1, 180);
  camera.position.set(0, 2.1, 18);

  // Soft forest light
  const ambient = new THREE.AmbientLight(0x36503d, 0.68);
  scene.add(ambient);
  const hemi = new THREE.HemisphereLight(0x9ac8ac, 0x1a2a18, 0.86);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffe6c0, 1.15);
  sun.position.set(18, 32, 8);
  scene.add(sun);
  const fill = new THREE.DirectionalLight(0x6a9a7a, 0.35);
  fill.position.set(-12, 8, -10);
  scene.add(fill);

  const root = new THREE.Group();
  scene.add(root);

  // Ground
  const groundMat = new THREE.MeshStandardMaterial({
    color: 0x1a2e1c,
    roughness: 0.95,
    metalness: 0.02,
  });
  const ground = new THREE.Mesh(new THREE.CircleGeometry(90, 64), groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0;
  root.add(ground);

  // Soft undergrowth disk (mossy center path)
  const pathMat = new THREE.MeshStandardMaterial({
    color: 0x243a28,
    roughness: 1,
    metalness: 0,
  });
  const path = new THREE.Mesh(new THREE.PlaneGeometry(6, 100), pathMat);
  path.rotation.x = -Math.PI / 2;
  path.position.set(0, 0.02, -20);
  root.add(path);

  // Shared tree geometries
  const trunkGeo = new THREE.CylinderGeometry(0.12, 0.22, 1, 6);
  trunkGeo.translate(0, 0.5, 0);
  const foliageGeos = [
    new THREE.ConeGeometry(1.1, 2.2, 7),
    new THREE.ConeGeometry(0.95, 1.9, 6),
    new THREE.ConeGeometry(1.25, 2.6, 8),
  ];
  foliageGeos.forEach((g) => g.translate(0, 0.55, 0));

  const trunkMat = new THREE.MeshStandardMaterial({
    color: 0x3a2a1c,
    roughness: 0.9,
    metalness: 0.05,
  });
  const foliageMats = [
    new THREE.MeshStandardMaterial({ color: 0x1f4a2e, roughness: 0.85, flatShading: true }),
    new THREE.MeshStandardMaterial({ color: 0x2a5c38, roughness: 0.8, flatShading: true }),
    new THREE.MeshStandardMaterial({ color: 0x163d28, roughness: 0.9, flatShading: true }),
    new THREE.MeshStandardMaterial({ color: 0x3a6b40, roughness: 0.82, flatShading: true }),
  ];

  const treeCount = mobile ? 56 : 170;
  const rand = mulberry32(20260718);
  const trees = new THREE.Group();
  root.add(trees);

  // Keep a clear corridor along +Z / -Z for the path
  function placeTree(): void {
    let x = 0;
    let z = 0;
    let tries = 0;
    do {
      x = (rand() - 0.5) * 70;
      z = (rand() - 0.5) * 90 - 10;
      tries += 1;
    } while (Math.abs(x) < 2.2 && z > -55 && z < 25 && tries < 12);

    const scale = 0.75 + rand() * 1.6;
    const leanX = (rand() - 0.5) * 0.12;
    const leanZ = (rand() - 0.5) * 0.12;
    const rotY = rand() * Math.PI * 2;

    const tree = new THREE.Group();
    tree.position.set(x, 0, z);
    tree.rotation.y = rotY;
    tree.rotation.x = leanX;
    tree.rotation.z = leanZ;
    tree.scale.setScalar(scale);

    const trunk = new THREE.Mesh(trunkGeo, trunkMat);
    trunk.scale.set(1, 1.1 + rand() * 1.4, 1);
    tree.add(trunk);

    const layers = 2 + Math.floor(rand() * 2);
    let y = 1.0 + rand() * 0.4;
    for (let L = 0; L < layers; L++) {
      const geo = foliageGeos[Math.floor(rand() * foliageGeos.length)];
      const mat = foliageMats[Math.floor(rand() * foliageMats.length)];
      const cone = new THREE.Mesh(geo, mat);
      const s = 1 - L * 0.18 + rand() * 0.08;
      cone.scale.set(s, s, s);
      cone.position.y = y;
      cone.rotation.y = rand() * Math.PI;
      tree.add(cone);
      y += 0.85 * s;
    }

    // slight distance-based fade via userData for optional wind
    tree.userData.phase = rand() * Math.PI * 2;
    tree.userData.amp = 0.015 + rand() * 0.02;
    trees.add(tree);
  }

  for (let i = 0; i < treeCount; i++) placeTree();

  // Sparse foreground markers (service “clearings”) — soft glowing orbs among trees
  const markers: THREE.Mesh[] = [];
  const markerGroup = new THREE.Group();
  root.add(markerGroup);
  const markerPositions = [
    new THREE.Vector3(-3.2, 1.2, 6),
    new THREE.Vector3(4.0, 1.0, 2),
    new THREE.Vector3(-2.5, 1.3, -4),
    new THREE.Vector3(3.5, 1.1, -10),
    new THREE.Vector3(-4.0, 1.4, -16),
    new THREE.Vector3(2.8, 1.2, -22),
  ];
  for (let i = 0; i < markerPositions.length; i++) {
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 12, 12),
      new THREE.MeshStandardMaterial({
        color: 0x1a3020,
        emissive: 0x4a9a60,
        emissiveIntensity: 0.35,
        roughness: 0.4,
        metalness: 0.2,
      }),
    );
    m.position.copy(markerPositions[i]);
    markerGroup.add(m);
    markers.push(m);

    const glow = new THREE.Mesh(
      new THREE.SphereGeometry(0.55, 12, 12),
      new THREE.MeshBasicMaterial({
        color: 0x5cbc70,
        transparent: true,
        opacity: 0.08,
        depthWrite: false,
      }),
    );
    glow.position.copy(markerPositions[i]);
    markerGroup.add(glow);
    m.userData.glow = glow;
  }

  // Firefly / spore particles
  const pCount = mobile ? 44 : 180;
  const pPos = new Float32Array(pCount * 3);
  for (let i = 0; i < pCount; i++) {
    pPos[i * 3] = (rand() - 0.5) * 50;
    pPos[i * 3 + 1] = 0.4 + rand() * 8;
    pPos[i * 3 + 2] = (rand() - 0.5) * 70;
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute("position", new THREE.BufferAttribute(pPos, 3));
  const particleMat = new THREE.PointsMaterial({
    color: 0xb8e0a8,
    size: mobile ? 0.06 : 0.045,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    sizeAttenuation: true,
  });
  const particles = new THREE.Points(pGeo, particleMat);
  root.add(particles);

  function applySceneTheme(next: "light" | "dark"): void {
    theme = next;
    const light = theme === "light";
    const bg = light ? 0xd7e8da : 0x0a1410;
    const fog = light ? 0xc5d9c8 : 0x0c1a14;
    renderer.setClearColor(bg, 1);
    renderer.toneMappingExposure = light ? 1.2 : 1.05;
    scene.background = new THREE.Color(bg);
    if (scene.fog instanceof THREE.FogExp2) {
      scene.fog.color.setHex(fog);
      scene.fog.density = light ? 0.018 : 0.028;
    }
    ambient.color.setHex(light ? 0xd7ead8 : 0x36503d);
    ambient.intensity = light ? 0.96 : 0.68;
    hemi.color.setHex(light ? 0xeef8ef : 0x9ac8ac);
    hemi.groundColor.setHex(light ? 0x9db58a : 0x1a2a18);
    hemi.intensity = light ? 0.98 : 0.86;
    sun.color.setHex(light ? 0xfff6df : 0xffe6c0);
    sun.intensity = light ? 1.4 : 1.15;
    fill.color.setHex(light ? 0xb6d9b8 : 0x6a9a7a);
    fill.intensity = light ? 0.55 : 0.35;
    groundMat.color.setHex(light ? 0x7fa86f : 0x1a2e1c);
    pathMat.color.setHex(light ? 0x94b884 : 0x243a28);
    trunkMat.color.setHex(light ? 0x6f5135 : 0x3a2a1c);
    const leaf = light
      ? [0x3f8f4a, 0x57a85c, 0x2f7340, 0x6db45f]
      : [0x1f4a2e, 0x2a5c38, 0x163d28, 0x3a6b40];
    foliageMats.forEach((m, i) => m.color.setHex(leaf[i] ?? leaf[0]));
    particleMat.color.setHex(light ? 0x4d6f38 : 0xb8e0a8);
    particleMat.opacity = light ? 0.28 : 0.55;
  }

  applySceneTheme(theme);

  // Camera path through the forest corridor
  const camStart = new THREE.Vector3(0, 2.15, 16);
  const camMid = new THREE.Vector3(0.6, 2.0, 4);
  const camDeep = new THREE.Vector3(-0.4, 1.85, -14);

  let progress = 0;
  let pointerX = 0;
  let pointerY = 0;
  let targetPX = 0;
  let targetPY = 0;
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let lookYaw = 0;
  let lookPitch = 0;
  let raf = 0;
  let disposed = false;
  let t0 = performance.now();
  let paused = document.hidden;
  let lastRender = 0;
  const camPos = camStart.clone();
  const lookTarget = new THREE.Vector3();

  function requestFrame(): void {
    if (!raf && !paused && !disposed) raf = requestAnimationFrame(frame);
  }

  function resize(): void {
    mobile = isMobile();
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / Math.max(1, h);
    camera.updateProjectionMatrix();
    requestFrame();
  }

  function setProgress(t: number): void {
    progress = Math.min(1, Math.max(0, t));
    requestFrame();
  }

  function setTheme(next: "light" | "dark"): void {
    applySceneTheme(next);
    requestFrame();
  }

  function setActiveService(index: number): void {
    markers.forEach((m, i) => {
      const on = i === index;
      const mat = m.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = on ? 1.4 : 0.35;
      mat.emissive.setHex(on ? 0x7dff9a : 0x4a9a60);
      m.scale.setScalar(on ? 1.5 : 1);
      const glow = m.userData.glow as THREE.Mesh | undefined;
      if (glow) {
        const gMat = glow.material as THREE.MeshBasicMaterial;
        gMat.opacity = on ? 0.22 : 0.08;
      }
    });
    requestFrame();
  }

  function sampleCamera(t: number, out: THREE.Vector3): THREE.Vector3 {
    const e = easeInOutCubic(t);
    if (e < 0.5) {
      return out.copy(camStart).lerp(camMid, easeInOutCubic(e / 0.5));
    }
    return out.copy(camMid).lerp(camDeep, easeInOutCubic((e - 0.5) / 0.5));
  }

  function onPointerDown(e: PointerEvent): void {
    if (!finePointer || e.pointerType === "touch") return;
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    renderer.domElement.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: PointerEvent): void {
    if (!finePointer || e.pointerType === "touch") return;
    const rect = renderer.domElement.getBoundingClientRect();
    targetPX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    targetPY = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    if (!dragging) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    lookYaw -= dx * 0.0035;
    lookPitch = Math.max(-0.35, Math.min(0.35, lookPitch - dy * 0.0025));
  }

  function onPointerUp(e: PointerEvent): void {
    if (!finePointer) return;
    dragging = false;
    try {
      renderer.domElement.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }

  function onVisibility(): void {
    paused = document.hidden;
    if (!paused && !disposed) {
      t0 = performance.now();
      requestFrame();
    }
  }

  if (finePointer) {
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("pointercancel", onPointerUp);
  }
  document.addEventListener("visibilitychange", onVisibility);

  function frame(now: number): void {
    raf = 0;
    if (disposed || paused) return;
    const interval = 1000 / (mobile ? 20 : 36);
    if (now - lastRender < interval) {
      raf = requestAnimationFrame(frame);
      return;
    }
    lastRender = now;
    const dt = Math.min(0.05, (now - t0) / 1000);
    t0 = now;

    if (finePointer) {
      pointerX += (targetPX - pointerX) * 0.05;
      pointerY += (targetPY - pointerY) * 0.05;
    }

    // Gentle wind — subsample on mobile
    if (!reduce) {
      const step = mobile ? 4 : 2;
      for (let i = 0; i < trees.children.length; i += step) {
        const tree = trees.children[i];
        const phase = tree.userData.phase as number;
        const amp = tree.userData.amp as number;
        tree.rotation.z = Math.sin(now * 0.0008 + phase) * amp;
        tree.rotation.x = Math.cos(now * 0.0006 + phase) * amp * 0.6;
      }
      particles.rotation.y += dt * 0.01;
      if (!mobile) {
        const arr = pGeo.getAttribute("position") as THREE.BufferAttribute;
        for (let i = 0; i < pCount; i++) {
          const y = arr.getY(i) + Math.sin(now * 0.001 + i) * 0.002;
          arr.setY(i, y > 9 ? 0.5 : y);
        }
        arr.needsUpdate = true;
      }
    }

    if (!mobile) {
      markers.forEach((m, i) => {
        m.position.y = markerPositions[i].y + Math.sin(now * 0.0018 + i) * 0.08;
        const glow = m.userData.glow as THREE.Mesh | undefined;
        if (glow) glow.position.copy(m.position);
      });
    }

    sampleCamera(progress, camPos);
    if (finePointer) {
      camPos.x += pointerX * 0.35 + Math.sin(lookYaw) * 0.5;
      camPos.y += pointerY * 0.2;
    }
    camera.position.lerp(camPos, reduce || mobile ? 0.18 : 0.08);

    lookTarget.set(
      camera.position.x + Math.sin(lookYaw) * 4 + pointerX * 1.2,
      camera.position.y + lookPitch * 3 + pointerY * 0.6,
      camera.position.z - 6,
    );
    camera.lookAt(lookTarget);

    if (scene.fog instanceof THREE.FogExp2) {
      const base = theme === "light" ? 0.016 : 0.022;
      const extra = theme === "light" ? 0.01 : 0.018;
      scene.fog.density = base + progress * extra;
    }
    sun.intensity =
      (theme === "light" ? 1.4 : 1.15) - progress * (theme === "light" ? 0.15 : 0.2);

    renderer.render(scene, camera);
    if (!mobile) raf = requestAnimationFrame(frame);
  }

  resize();
  requestFrame();
  options.onReady?.();

  function dispose(): void {
    disposed = true;
    cancelAnimationFrame(raf);
    document.removeEventListener("visibilitychange", onVisibility);
    if (finePointer) {
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("pointercancel", onPointerUp);
    }
    renderer.dispose();
    trunkGeo.dispose();
    foliageGeos.forEach((g) => g.dispose());
    trunkMat.dispose();
    foliageMats.forEach((m) => m.dispose());
    pGeo.dispose();
    particleMat.dispose();
    scene.traverse((obj: THREE.Object3D) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry && mesh.geometry !== trunkGeo && !foliageGeos.includes(mesh.geometry as THREE.ConeGeometry)) {
        // shared geos handled above; dispose unique ones
        if (
          mesh.geometry.type === "CircleGeometry" ||
          mesh.geometry.type === "PlaneGeometry" ||
          mesh.geometry.type === "SphereGeometry"
        ) {
          mesh.geometry.dispose();
        }
      }
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else if (
        mat &&
        mat !== trunkMat &&
        !foliageMats.includes(mat as THREE.MeshStandardMaterial)
      ) {
        mat.dispose();
      }
    });
    if (renderer.domElement.parentElement === container) {
      container.removeChild(renderer.domElement);
    }
  }

  return { setProgress, setActiveService, setTheme, resize, dispose };
}
